import {
  ReactNode,
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useTransactions } from "../hooks/useTransactions";
import { TransactionResponse } from "../services/transactionService/transaction";
import { useDebounce } from "../hooks/useDebounce";
import { useTransactionChartHook } from "../hooks/useTransactionChartHook";
import { TransactionChartResponse } from "../services/transactionService/transactionChart";
import { useLocation } from "react-router-dom";

interface FiltersType {
  sellerIds?: string;
  customerIds?: string;
  teamIds?: string;
  startDate?: string;
  endDate?: string;
  status?: string;
  type?: string;
  activity?: string;
}

interface TransactionContextValue {
  setSkip: (value: number) => void;
  transaction: {
    isLoading: boolean;
    isFetching: boolean;
    data: TransactionResponse | undefined;
    fetchNextPage?: () => void;
    hasNextPage?: boolean;
    isFetchingNextPage: boolean;
  };
  setSearchValue: React.Dispatch<React.SetStateAction<string | undefined>>;
  debouncedSearchValue?: string;
  transactionChart: {
    isLoading: boolean;
    isFetching: boolean;
    data: TransactionChartResponse | undefined;
  };
  setFilterParams: (value: FiltersType) => void;
  refetch: () => void;
  isSearchActive?: boolean;
  searchRange: {
    startDate: Date;
    endDate: Date;
  };
  searchFullPeriod: () => void;
  isFullPeriodSearch: boolean;
}
export const TransactionContext = createContext({} as TransactionContextValue);

const SEARCH_DAYS = Number(import.meta.env.VITE_TRANSACTION_SEARCH_DAYS ?? 60);

export function TransactionProvider({ children }: { children: ReactNode }) {
  const [searchValue, setSearchValue] = useState<string | undefined>(undefined);
  const debouncedSearchValue = useDebounce(500, searchValue);

  const isSearchActive = !!debouncedSearchValue;

  const [isFullPeriodSearch, setIsFullPeriodSearch] = useState(false);

  const location = useLocation();

  const interval = ["/transacoes"].includes(location.pathname)
    ? 5 * 60 * 1000
    : false;

  const setSkip = (value: number) => {
    setParams((state) => ({ ...state, skip: value }));
  };

  const setFilterParams = (value: FiltersType) => {
    setParams(value);
    setParamsChart(value);
  };

  const { refetch, setParams, ...transaction } = useTransactions({
    interval,
    useInfiniteScroll: true,
  });

  const { setParams: setParamsChart, ...transactionChart } =
    useTransactionChartHook({ interval });

  const setSearch = useCallback(() => {
    const now = new Date();

    const endDate = now.toISOString();

    const start = new Date();
    start.setDate(start.getDate() - SEARCH_DAYS);

    const startDate = start.toISOString();

    setIsFullPeriodSearch(false);

    setParams((state) => ({
      ...state,
      search: debouncedSearchValue,
      startDate,
      endDate,
    }));
  }, [debouncedSearchValue, setParams]);

  const searchFullPeriod = useCallback(() => {
    setIsFullPeriodSearch(true);
    setParams((state) => ({
      ...state,
      search: debouncedSearchValue,
      startDate: undefined,
      endDate: undefined,
    }));
  }, [debouncedSearchValue, setParams]);

  const searchRange = useMemo(() => {
    const end = new Date();
    const start = new Date();
    start.setDate(start.getDate() - SEARCH_DAYS);

    return {
      startDate: start,
      endDate: end,
    };
  }, []);

  const resetSearch = useCallback(() => {
    setSearchValue(undefined);
    setIsFullPeriodSearch(false);
    setParams((state) => ({ ...state, search: undefined }));
  }, [setParams]);

  useEffect(() => {
    setSearch();
  }, [setSearch]);

  useEffect(() => {
    resetSearch();
  }, [location.pathname, resetSearch]);

  return (
    <TransactionContext.Provider
      value={{
        setSkip,
        transaction,
        setSearchValue,
        debouncedSearchValue,
        transactionChart,
        setFilterParams,
        refetch: refetch || (() => {}),
        isSearchActive,
        searchRange,
        searchFullPeriod,
        isFullPeriodSearch,
      }}
    >
      {children}
    </TransactionContext.Provider>
  );
}
